import { useInterfaceDesign } from "../design/InterfaceDesign";
import { designTokens } from "../design/tokens";
import { getValueTypeColor } from "../../../util/src/blueprint/index.js";
import type { BehaviorNode, Port } from "../../../util/src/blueprint/types.js";
import type { ListValue } from "../../../util/src/blueprint/types.js";
import type {
  LayoutListTargetOption,
  LayoutListTargetValue,
  LocationValue,
  TransformValue,
  VariableValueType,
} from "../../../util/src/blueprint/types.js";
import { FONT_WEIGHT_VALUES } from "../../../util/src/blueprint/types.js";
import { FONT_FAMILY_VALUES } from "../../../util/src/blueprint/types.js";
import {
  TEXT_ALIGN_VALUES,
  TEXT_DECORATION_LINE_VALUES,
  TEXT_DECORATION_STYLE_VALUES,
  TEXT_TRANSFORM_VALUES,
  WHITE_SPACE_VALUES,
} from "../../../util/src/blueprint/types.js";
import {
  formatTextDecorationLineSelection,
  normalizeAnimationDirectionValue,
  normalizeAnimationEasingValue,
  normalizeAnimationFillModeValue,
  normalizeFontFamilyValue,
  normalizeFontWeightValue,
  normalizeTextAlignValue,
  normalizeTextDecorationStyleValue,
  normalizeTextTransformValue,
  normalizeWhiteSpaceValue,
  parseTextDecorationLineSelection,
} from "../../../util/src/blueprint/method.js";
import dico from "../../../util/src/language/dico.js";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import type { SelectChangeEvent } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { enUS, frFR } from "@mui/x-date-pickers/locales";
import LabelImportantIcon from "@mui/icons-material/LabelImportant";
import { useCallback, useContext, useMemo } from "react";
import type { MouseEvent } from "react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { getLanguage } from "../../../util/src/language/dico.js";
import ColorPicker from "../components/ColorPicker.js";
import BorderPicker, { BorderConfig } from "../components/BorderPicker.js";
import BorderRadiusPicker, {
  BorderRadiusConfig,
} from "../components/BorderRadiusPicker.js";
import SpacingPicker, { SpacingConfig } from "../components/SpacingPicker.js";
import FilterPicker from "../components/FilterPicker.js";
import BoxShadowPicker from "../components/BoxShadowPicker.js";
import TextShadowPicker from "../components/TextShadowPicker.js";
import GradientPicker from "../components/GradientPicker.js";
import { FilterSvgPickerContext } from "./FilterSvgPickerContext.js";
import { BehaviorFontContext } from "./BehaviorFontContext.js";

const getCompositePortNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
};

const compositeTripleInputSx = {
  width: 68,
  "& .MuiInputBase-input": {
    py: 0.4,
    px: 0.9,
    fontSize: 12,
  },
} as const;

export interface PortHandleProps {
  port: Port;
  node: BehaviorNode;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onMouseDown?: () => void;
  onMouseUp?: () => void;
  showValueInput?: boolean;
  isConnected?: boolean;
  onValueChange?: (
    value:
      | string
      | number
      | boolean
      | ListValue
      | BorderConfig
      | BorderRadiusConfig
      | SpacingConfig
      | LayoutListTargetValue
      | LocationValue
      | TransformValue,
  ) => void;
  onValueBlur?: (
    value:
      | string
      | number
      | boolean
      | ListValue
      | BorderConfig
      | BorderRadiusConfig
      | SpacingConfig
      | LayoutListTargetValue
      | LocationValue
      | TransformValue,
  ) => void;
  onContextMenu?: (event: MouseEvent) => void;
  onListItemValueTypeChange?: (
    nodeId: string,
    valueType: Exclude<VariableValueType, "list">,
  ) => void;
  layoutListTargetOptions?: LayoutListTargetOption[];
  showLabel?: boolean;
}

export const PortHandle = ({
  port,
  setPortsMap,
  onMouseDown,
  onMouseUp,
  node,
  showValueInput = false,
  isConnected = false,
  onValueChange,
  onValueBlur,
  onContextMenu,
  onListItemValueTypeChange,
  layoutListTargetOptions,
  showLabel = node.type !== "operator",
}: PortHandleProps) => {
  const { mode, colorMode } = useInterfaceDesign();
  const modern = mode === "modern";
  const palette = designTokens[colorMode];
  const isInput = port.direction === "input";
  const language = getLanguage();
  const isFrench = language.includes("fr");

  const shouldRenderInput = showValueInput && (!isConnected || !isInput);
  const onSelectSvgFile = useContext(FilterSvgPickerContext);
  const { customFontFamilies, onPickFont } = useContext(BehaviorFontContext);
  const fontFamilyValues = useMemo(() => {
    const values: string[] = [...FONT_FAMILY_VALUES];
    for (const customFamily of customFontFamilies) {
      if (!values.includes(customFamily)) {
        values.push(customFamily);
      }
    }
    return values;
  }, [customFontFamilies]);
  const customFontFamilyValues = useMemo(
    () =>
      customFontFamilies
        .filter((family, index, source) => source.indexOf(family) === index)
        .filter((family) => !FONT_FAMILY_VALUES.includes(family as never)),
    [customFontFamilies],
  );
  const resolveFontFamilySelection = useCallback(
    (raw: unknown): string => {
      const candidate = String(raw ?? "")
        .trim()
        .replace(/^['"]|['"]$/g, "");
      if (candidate && fontFamilyValues.includes(candidate)) {
        return candidate;
      }
      return normalizeFontFamilyValue(raw);
    },
    [fontFamilyValues],
  );

  const hitboxExtension = 12;
  const hitboxVertical = 10;

  const portRef = useCallback(
    (el: HTMLDivElement | null) => {
      setPortsMap(node.id, port.id, el);
    },
    [setPortsMap, node.id, port.id],
  );

  const point = (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {/* Visual dot - pointer events disabled so the overlay handles all interactions */}
      <span
        ref={portRef}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: port.valueType === "flow" ? 18 : 10,
          height: port.valueType === "flow" ? 18 : 10,
          borderRadius: port.valueType === "flow" ? 0 : "50%",
          backgroundColor:
            port.valueType === "flow"
              ? "transparent"
              : getValueTypeColor(port.valueType),
          flexShrink: 0,
          cursor: "pointer",
          pointerEvents: "none",
        }}
      >
        {port.valueType === "flow" ? (
          <LabelImportantIcon
            sx={{ fontSize: 18, color: modern ? palette.text : "#111111" }}
          />
        ) : null}
      </span>
      {/* Invisible extended hitbox overlay */}
      <span
        data-port-handle
        onMouseDown={(event) => {
          if (event.button !== 0) return;
          onMouseDown?.();
        }}
        onMouseUp={(event) => {
          if (event.button !== 0) return;
          onMouseUp?.();
        }}
        onContextMenu={(event) => {
          if (!onContextMenu) return;
          event.preventDefault();
          event.stopPropagation();
          onContextMenu(event);
        }}
        style={{
          position: "absolute",
          top: -hitboxVertical,
          bottom: -hitboxVertical,
          left: isInput ? -hitboxExtension : 0,
          right: isInput ? 0 : -hitboxExtension,
          cursor: "pointer",
        }}
      />
    </span>
  );

  const input = (() => {
    if (!shouldRenderInput) {
      return null;
    }
    if (port.valueType === "boolean") {
      return (
        <Checkbox
          checked={Boolean(port.value)}
          onChange={(event) => onValueChange?.(event.target.checked)}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          size="small"
          sx={{ p: 0.25 }}
        />
      );
    }
    if (port.valueType === "number") {
      return (
        <TextField
          type="number"
          value={typeof port.value === "number" ? port.value : ""}
          onChange={(event) => onValueChange?.(Number(event.target.value))}
          onBlur={(event) => onValueBlur?.(Number(event.target.value))}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          size="small"
          variant="outlined"
          sx={{
            width: 88,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        />
      );
    }
    if (port.valueType === "list") {
      const listValueTypeChoices: Array<Exclude<VariableValueType, "list">> = [
        "number",
        "string",
        "boolean",
        "date",
        "fontFamily",
        "fontWeight",
        "textAlign",
        "textTransform",
        "whiteSpace",
        "textDecorationLine",
        "textDecorationColor",
        "textDecorationStyle",
        "textDecorationThickness",
        "animationDirection",
        "animationFill",
        "animationEasing",
        "color",
        "gradient",
        "filter",
        "boxShadow",
        "textShadow",
        "border",
        "borderRadius",
        "spacing",
        "location",
        "transform",
        "node",
        "object",
      ];
      const inputTypeLabels = dico().behavior.nodeList.inputTypes as Record<
        string,
        string
      >;
      const selectedListItemType =
        (node.type === "variable" && node.valueType === "list"
          ? node.listItemValueType
          : node.type === "operator" &&
              (node.operator === "push" ||
                node.operator === "get" ||
                node.operator === "remove" ||
                node.operator === "find" ||
                node.operator === "findByCondition" ||
                node.operator === "filter" ||
                node.operator === "sortBy" ||
                node.operator === "sort" ||
                node.operator === "take" ||
                node.operator === "currentItem" ||
                node.operator === "comparator")
            ? node.listItemValueType
            : undefined) ?? "string";
      return (
        <TextField
          select
          size="small"
          value={selectedListItemType}
          onChange={(event) => {
            const nextType = event.target.value as Exclude<
              VariableValueType,
              "list"
            >;
            onListItemValueTypeChange?.(node.id, nextType);
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          variant="outlined"
          sx={{
            width: 170,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        >
          {listValueTypeChoices.map((valueType) => (
            <MenuItem key={valueType} value={valueType}>
              {inputTypeLabels[valueType] ?? valueType}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (port.valueType === "layoutListTarget") {
      const targetOptions = (layoutListTargetOptions ??
        []) as LayoutListTargetOption[];
      const currentNodeId =
        port.value &&
        typeof port.value === "object" &&
        "kind" in port.value &&
        "nodeId" in port.value &&
        (port.value as { kind?: unknown; nodeId?: unknown }).kind ===
          "layoutList"
          ? String((port.value as { nodeId?: unknown }).nodeId ?? "")
          : "";
      const selectedValue =
        currentNodeId &&
        targetOptions.some((option) => option.nodeId === currentNodeId)
          ? currentNodeId
          : "";
      return (
        <TextField
          select
          value={selectedValue}
          placeholder={dico().behavior.actions.setTarget}
          onChange={(event) =>
            onValueChange?.({
              kind: "layoutList",
              nodeId: event.target.value,
            })
          }
          onBlur={(event) =>
            onValueBlur?.({
              kind: "layoutList",
              nodeId: event.target.value,
            })
          }
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          size="small"
          variant="outlined"
          sx={{
            width: 160,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        >
          <MenuItem value="">{dico().behavior.actions.setTarget}</MenuItem>
          {targetOptions.map((option) => (
            <MenuItem key={option.nodeId} value={option.nodeId}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (port.valueType === "date") {
      return (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={isFrench ? "fr" : "en"}
          localeText={
            isFrench
              ? frFR.components.MuiLocalizationProvider.defaultProps.localeText
              : enUS.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <DatePicker
            value={
              typeof port.value === "string" && port.value
                ? dayjs(port.value)
                : null
            }
            onChange={(value) => {
              if (!value || !value.isValid()) {
                onValueChange?.("");
                return;
              }
              onValueChange?.(value.format("YYYY-MM-DD"));
            }}
            format={isFrench ? "DD/MM/YYYY" : "MM/DD/YYYY"}
            slotProps={{
              textField: {
                size: "small",
                variant: "outlined",
                onMouseDown: (event) => event.stopPropagation(),
                onMouseUp: (event) => event.stopPropagation(),
                sx: {
                  width: 140,
                  "& .MuiInputBase-input": {
                    py: 0.4,
                    px: 0.75,
                    fontSize: 12,
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
      );
    }
    if (port.valueType === "color") {
      return (
        <div
          style={{ width: "100%" }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <ColorPicker
            value={typeof port.value === "string" ? port.value : ""}
            onChange={(nextColor) => onValueChange?.(nextColor)}
          />
        </div>
      );
    }
    if (port.valueType === "border") {
      const fallback: BorderConfig = {
        border: {
          top: "1px solid #000000",
          right: "1px solid #000000",
          bottom: "1px solid #000000",
          left: "1px solid #000000",
        },
      };
      const borderValue =
        port.value &&
        typeof port.value === "object" &&
        "border" in port.value &&
        (port.value as BorderConfig).border
          ? (port.value as BorderConfig)
          : fallback;
      return (
        <div
          style={{ width: "100%", minWidth: 110 }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <BorderPicker
            value={borderValue}
            onChange={(nextBorder) => onValueChange?.(nextBorder)}
          />
        </div>
      );
    }
    if (port.valueType === "filter") {
      return (
        <div
          style={{ width: "100%", minWidth: 130 }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <FilterPicker
            value={typeof port.value === "string" ? port.value : "none"}
            onChange={(nextValue) => onValueChange?.(nextValue)}
            onSelectSvgFile={onSelectSvgFile}
          />
        </div>
      );
    }
    if (port.valueType === "gradient") {
      return (
        <div
          style={{ width: "100%", minWidth: 130 }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <GradientPicker
            value={
              typeof port.value === "string"
                ? port.value
                : "linear-gradient(90deg, #000000 0%, #ffffff 100%)"
            }
            onChange={(nextValue) => onValueChange?.(nextValue)}
          />
        </div>
      );
    }
    if (port.valueType === "boxShadow") {
      return (
        <div
          style={{ width: "100%", minWidth: 130 }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <BoxShadowPicker
            value={typeof port.value === "string" ? port.value : "none"}
            onChange={(nextValue) => onValueChange?.(nextValue)}
          />
        </div>
      );
    }
    if (port.valueType === "textShadow") {
      return (
        <div
          style={{ width: "100%", minWidth: 130 }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <TextShadowPicker
            value={typeof port.value === "string" ? port.value : "none"}
            onChange={(nextValue) => onValueChange?.(nextValue)}
          />
        </div>
      );
    }
    if (port.valueType === "borderRadius") {
      const fallback: BorderRadiusConfig = {
        radius: {
          topLeft: "0px",
          topRight: "0px",
          bottomRight: "0px",
          bottomLeft: "0px",
        },
      };
      const borderRadiusValue =
        port.value &&
        typeof port.value === "object" &&
        "radius" in port.value &&
        (port.value as BorderRadiusConfig).radius
          ? (port.value as BorderRadiusConfig)
          : fallback;
      return (
        <div
          style={{ width: "100%", minWidth: 110 }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <BorderRadiusPicker
            value={borderRadiusValue}
            onChange={(nextBorderRadius) => onValueChange?.(nextBorderRadius)}
          />
        </div>
      );
    }
    if (port.valueType === "spacing") {
      const fallback: SpacingConfig = {
        margin: { scope: "all", value: 0 },
        padding: { scope: "all", value: 0 },
      };
      const spacingValue =
        port.value &&
        typeof port.value === "object" &&
        "margin" in port.value &&
        "padding" in port.value
          ? (port.value as SpacingConfig)
          : fallback;
      return (
        <div
          style={{ width: "100%", minWidth: 130 }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <SpacingPicker
            value={spacingValue}
            onChange={(nextSpacing) => onValueChange?.(nextSpacing)}
          />
        </div>
      );
    }
    if (port.valueType === "animationDirection") {
      const labels = dico().behavior.animation.directionChoices;
      const selected = normalizeAnimationDirectionValue(port.value);
      return (
        <TextField
          select
          size="small"
          value={selected}
          onChange={(event) => onValueChange?.(event.target.value)}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          variant="outlined"
          sx={{
            width: 168,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        >
          <MenuItem value="normal">{labels.normal}</MenuItem>
          <MenuItem value="reverse">{labels.reverse}</MenuItem>
          <MenuItem value="alternate">{labels.alternate}</MenuItem>
          <MenuItem value="alternate-reverse">
            {labels.alternateReverse}
          </MenuItem>
        </TextField>
      );
    }
    if (port.valueType === "animationFill") {
      const labels = dico().behavior.animation.fillModeChoices;
      const selected = normalizeAnimationFillModeValue(port.value);
      return (
        <TextField
          select
          size="small"
          value={selected}
          onChange={(event) => onValueChange?.(event.target.value)}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          variant="outlined"
          sx={{
            width: 168,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        >
          <MenuItem value="none">{labels.none}</MenuItem>
          <MenuItem value="forwards">{labels.forwards}</MenuItem>
          <MenuItem value="backwards">{labels.backwards}</MenuItem>
          <MenuItem value="both">{labels.both}</MenuItem>
        </TextField>
      );
    }
    if (port.valueType === "animationEasing") {
      const labels = dico().behavior.animation.easingChoices;
      const selected = normalizeAnimationEasingValue(port.value);
      return (
        <TextField
          select
          size="small"
          value={selected}
          onChange={(event) => onValueChange?.(event.target.value)}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          variant="outlined"
          sx={{
            width: 168,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        >
          <MenuItem value="linear">{labels.linear}</MenuItem>
          <MenuItem value="ease">{labels.ease}</MenuItem>
          <MenuItem value="ease-in">{labels["ease-in"]}</MenuItem>
          <MenuItem value="ease-out">{labels["ease-out"]}</MenuItem>
          <MenuItem value="ease-in-out">{labels["ease-in-out"]}</MenuItem>
        </TextField>
      );
    }
    if (port.valueType === "fontWeight") {
      const selected = normalizeFontWeightValue(port.value);
      const labels = dico().behavior.fontWeightChoices;
      return (
        <TextField
          select
          size="small"
          value={selected}
          onChange={(event) => onValueChange?.(event.target.value)}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          variant="outlined"
          sx={{
            width: 132,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        >
          {FONT_WEIGHT_VALUES.map((fontWeight) => (
            <MenuItem key={fontWeight} value={fontWeight}>
              {labels[fontWeight]}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (port.valueType === "fontFamily") {
      const selected = resolveFontFamilySelection(port.value);
      const labels = dico().behavior.fontFamilyChoices as Record<
        string,
        string
      >;
      return (
        <TextField
          select
          size="small"
          value={selected}
          onChange={(event) => {
            if (event.target.value === "__add_font__") return;
            onValueChange?.(event.target.value);
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          variant="outlined"
          sx={{
            width: 180,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
              fontFamily: selected,
            },
          }}
        >
          {onPickFont ? (
            <MenuItem
              value="__add_font__"
              onClick={async (event) => {
                event.preventDefault();
                event.stopPropagation();
                const uploadedFontFamily = await onPickFont();
                if (!uploadedFontFamily) return;
                onValueChange?.(uploadedFontFamily);
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  aria-label={
                    dico().licensedPortal.layoutEditor.properties
                      .styleFontFamily
                  }
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    border: `1px solid ${modern ? palette.border : "#000000"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: modern ? palette.text : "#000000",
                  }}
                >
                  <AddIcon fontSize="small" />
                </Box>
              </Box>
            </MenuItem>
          ) : null}
          {customFontFamilyValues.map((fontFamily) => (
            <MenuItem
              key={fontFamily}
              value={fontFamily}
              sx={{ fontFamily, fontSize: 12 }}
            >
              {labels[fontFamily] ?? fontFamily}
            </MenuItem>
          ))}
          {customFontFamilyValues.length > 0 ? <Divider /> : null}
          {fontFamilyValues
            .filter(
              (fontFamily) => !customFontFamilyValues.includes(fontFamily),
            )
            .map((fontFamily) => (
              <MenuItem
                key={fontFamily}
                value={fontFamily}
                sx={{ fontFamily, fontSize: 12 }}
              >
                {labels[fontFamily] ?? fontFamily}
              </MenuItem>
            ))}
        </TextField>
      );
    }
    if (port.valueType === "textAlign") {
      const selected = normalizeTextAlignValue(port.value);
      const labels = dico().behavior.textAlignChoices;
      return (
        <TextField
          select
          size="small"
          value={selected}
          onChange={(event) => onValueChange?.(event.target.value)}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          variant="outlined"
          sx={{
            width: 132,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        >
          {TEXT_ALIGN_VALUES.map((textAlign) => (
            <MenuItem key={textAlign} value={textAlign}>
              {labels[textAlign]}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (port.valueType === "textTransform") {
      const selected = normalizeTextTransformValue(port.value);
      const labels = dico().behavior.textTransformChoices;
      return (
        <TextField
          select
          size="small"
          value={selected}
          onChange={(event) => onValueChange?.(event.target.value)}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          variant="outlined"
          sx={{
            width: 132,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        >
          {TEXT_TRANSFORM_VALUES.map((textTransform) => (
            <MenuItem key={textTransform} value={textTransform}>
              {labels[textTransform]}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (port.valueType === "whiteSpace") {
      const selected = normalizeWhiteSpaceValue(port.value);
      const labels = dico().behavior.whiteSpaceChoices;
      return (
        <TextField
          select
          size="small"
          value={selected}
          onChange={(event) => onValueChange?.(event.target.value)}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          variant="outlined"
          sx={{
            width: 168,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        >
          {WHITE_SPACE_VALUES.map((whiteSpace) => (
            <MenuItem key={whiteSpace} value={whiteSpace}>
              {labels[whiteSpace]}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (port.valueType === "textDecorationLine") {
      const lineChoices = dico().behavior.textDecorationLineChoices;
      const selected = parseTextDecorationLineSelection(port.value);
      const selectable = TEXT_DECORATION_LINE_VALUES.filter(
        (value) => value !== "none",
      );
      return (
        <FormControl
          size="small"
          sx={{ width: 200 }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <Select<string[]>
            multiple
            value={selected}
            onChange={(event: SelectChangeEvent<string[]>) => {
              const nextRaw = event.target.value;
              const nextLines =
                typeof nextRaw === "string" ? nextRaw.split(",") : [...nextRaw];
              onValueChange?.(formatTextDecorationLineSelection(nextLines));
            }}
            input={<OutlinedInput />}
            renderValue={(entries) => {
              const selectedLines = entries as string[];
              if (selectedLines.length === 0) {
                return lineChoices.none ?? "none";
              }
              return selectedLines
                .map(
                  (line) =>
                    lineChoices[line as keyof typeof lineChoices] ?? line,
                )
                .join(", ");
            }}
            sx={{
              fontSize: 12,
              "& .MuiSelect-select": { py: 0.4, px: 0.75 },
            }}
          >
            {selectable.map((line) => (
              <MenuItem key={line} value={line} sx={{ fontSize: 12 }}>
                {lineChoices[line as keyof typeof lineChoices] ?? line}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (port.valueType === "textDecorationStyle") {
      const selected = normalizeTextDecorationStyleValue(port.value);
      const labels = dico().behavior.textDecorationStyleChoices;
      return (
        <TextField
          select
          size="small"
          value={selected}
          onChange={(event) =>
            onValueChange?.(
              normalizeTextDecorationStyleValue(event.target.value),
            )
          }
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          variant="outlined"
          sx={{
            width: 132,
            "& .MuiInputBase-input": {
              py: 0.4,
              px: 0.75,
              fontSize: 12,
            },
          }}
        >
          {TEXT_DECORATION_STYLE_VALUES.map((textDecorationStyle) => (
            <MenuItem key={textDecorationStyle} value={textDecorationStyle}>
              {labels[textDecorationStyle]}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (port.valueType === "location") {
      const locationValue =
        port.value && typeof port.value === "object"
          ? (port.value as Partial<LocationValue>)
          : {};
      return (
        <div
          style={{ display: "flex", alignItems: "center", gap: 6 }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <TextField
            size="small"
            type="number"
            value={getCompositePortNumber(locationValue.x)}
            onChange={(event) =>
              onValueChange?.({
                x: Number(event.target.value),
                y: getCompositePortNumber(locationValue.y),
                z: getCompositePortNumber(locationValue.z),
              })
            }
            sx={compositeTripleInputSx}
          />
          <TextField
            size="small"
            type="number"
            value={getCompositePortNumber(locationValue.y)}
            onChange={(event) =>
              onValueChange?.({
                x: getCompositePortNumber(locationValue.x),
                y: Number(event.target.value),
                z: getCompositePortNumber(locationValue.z),
              })
            }
            sx={compositeTripleInputSx}
          />
          <TextField
            size="small"
            type="number"
            value={getCompositePortNumber(locationValue.z)}
            onChange={(event) =>
              onValueChange?.({
                x: getCompositePortNumber(locationValue.x),
                y: getCompositePortNumber(locationValue.y),
                z: Number(event.target.value),
              })
            }
            sx={compositeTripleInputSx}
          />
        </div>
      );
    }
    if (port.valueType === "transform") {
      const transformValue =
        port.value && typeof port.value === "object"
          ? (port.value as Partial<TransformValue>)
          : {};
      return (
        <div
          style={{ display: "flex", alignItems: "center", gap: 6 }}
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
        >
          <TextField
            size="small"
            type="number"
            value={getCompositePortNumber(transformValue.width)}
            onChange={(event) =>
              onValueChange?.({
                width: Number(event.target.value),
                height: getCompositePortNumber(transformValue.height),
                rotation: getCompositePortNumber(transformValue.rotation),
              })
            }
            sx={compositeTripleInputSx}
          />
          <TextField
            size="small"
            type="number"
            value={getCompositePortNumber(transformValue.height)}
            onChange={(event) =>
              onValueChange?.({
                width: getCompositePortNumber(transformValue.width),
                height: Number(event.target.value),
                rotation: getCompositePortNumber(transformValue.rotation),
              })
            }
            sx={compositeTripleInputSx}
          />
          <TextField
            size="small"
            type="number"
            value={getCompositePortNumber(transformValue.rotation)}
            onChange={(event) =>
              onValueChange?.({
                width: getCompositePortNumber(transformValue.width),
                height: getCompositePortNumber(transformValue.height),
                rotation: Number(event.target.value),
              })
            }
            sx={compositeTripleInputSx}
          />
        </div>
      );
    }
    return (
      <TextField
        type="text"
        value={typeof port.value === "string" ? port.value : ""}
        onChange={(event) => onValueChange?.(event.target.value)}
        onBlur={(event) => onValueBlur?.(event.target.value)}
        onMouseDown={(event) => event.stopPropagation()}
        onMouseUp={(event) => event.stopPropagation()}
        size="small"
        variant="outlined"
        sx={{
          width: 112,
          "& .MuiInputBase-input": {
            py: 0.4,
            px: 0.75,
            fontSize: 12,
          },
        }}
      />
    );
  })();

  return (
    <div
      data-port-id={port.id}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: isInput ? "flex-start" : "flex-end",
        gap: 8,
        minHeight: 32,
        cursor: "pointer",
        userSelect: "none",
        width: "100%",
        color: "#d9d9d9",
        fontSize: 12,
        padding: "4px 8px",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      {isInput && point}
      {input}
      {showLabel && (
        <span
          style={{
            textAlign: isInput ? "left" : "right",
            color: modern ? palette.muted : "#4b5563",
          }}
        >
          {port.label}
        </span>
      )}
      {!isInput && point}
    </div>
  );
};
